import React, { useRef, useEffect, useState } from "react";

import { Button } from "../ui/button";
import { lerPreferencia, gravarPreferencia } from "../../services/preferencias";

const LS_NAME = "audioMessageRate";
const VELOCIDADES = [0.5, 1, 1.5, 2];

const formatarDuracao = (segundos) => {
	if (!Number.isFinite(segundos) || segundos <= 0 || segundos === Infinity || segundos === -Infinity) return null;
	const total = Math.round(segundos);
	const minutos = Math.floor(total / 60);
	const resto = total % 60;
	return `${minutos}:${String(resto).padStart(2, "0")}`;
};

export default function Audio({ url }) {
	const audioRef = useRef(null);
	const [audioRate, setAudioRate] = useState(
		parseFloat(lerPreferencia(LS_NAME, "1"))
	);
	const [showButtonRate, setShowButtonRate] = useState(false);
	const [duracao, setDuracao] = useState(null);
	const [duracaoCarregando, setDuracaoCarregando] = useState(true);

	const tentarLerDuracaoAgora = () => {
		const el = audioRef.current;
		if (!el) return;
		// .duration pode ser NaN / Infinity / 0 antes de metadata.
		const d = el.duration;
		if (Number.isFinite(d) && d > 0) {
			setDuracao(d);
			setDuracaoCarregando(false);
		}
	};

	// Toda vez que a URL mudar: reseta estado, força .load() para recarregar
	// metadados do NOVO áudio, e tenta ler a duração em 3 passos (existem
	// arquivos onde loadedmetadata não dispara mas a duração já está disponível
	// depois de 100~300ms — comum em aac/mp4 do WhatsApp).
	useEffect(() => {
		if (!url) return undefined;
		const el = audioRef.current;
		// Os 3 setStates abaixo NÃO podem ser síncronos no corpo do effect
		// (regra react-hooks/set-state-in-effect: cascata de re-render).
		// Enfileirar em queueMicrotask: roda no mesmo tick de pintura mas após
		// sair do corpo do effect — 100% permitido e sem re-render cascata.
		queueMicrotask(() => {
			setDuracao(null);
			setDuracaoCarregando(true);
			setShowButtonRate(false);
		});
		// Força o navegador re-avaliar o source do áudio quando url muda (essencial
		// quando o componente Audio é reutilizado entre mensagens diferentes na
		// mesma lista — sem .load() ele continuaria com os metadados do áudio
		// anterior e a duração nunca atualizava).
		try {
			el?.load();
		} catch {
			/* no-op: alguns navegadores bloqueiam load() automático em data URIs */
		}

		tentarLerDuracaoAgora();
		const t1 = setTimeout(tentarLerDuracaoAgora, 80);
		const t2 = setTimeout(tentarLerDuracaoAgora, 220);
		const t3 = setTimeout(tentarLerDuracaoAgora, 500);
		// Fallback final: após 6 segundos, para de piscar o 0:00 mesmo que nunca
		// chegue a duração (áudio corrompido por exemplo).
		const tFallback = setTimeout(() => setDuracaoCarregando(false), 6000);

		// === Audio V4: polling para arquivos MP4 WhatsApp com MOOV no fim ===
		// Muitos áudios do WhatsApp são gravados com o átomo MOOV (metadados de
		// duração/codec) NO FIM do arquivo. Eventos loadedmetadata /
		// durationchange NÃO disparam até o navegador baixar o arquivo INTEIRO.
		// Polling de 250ms por até 3s (12x) pega a duração assim que ela fica
		// disponível, sem depender de eventos.
		let tentativasPolling = 0;
		const polling = setInterval(() => {
			tentarLerDuracaoAgora();
			tentativasPolling += 1;
			const agora = audioRef.current;
			const dur = agora?.duration;
			const terminou =
				tentativasPolling >= 12 ||
				(Number.isFinite(dur) && dur > 0);
			if (terminou) clearInterval(polling);
		}, 250);

		// Hack silencioso: tenta dar play() e imediatamente pause().
		// Isso força o navegador a ABIR a conexão HTTP e baixar os primeiros
		// bytes do arquivo — incluindo o início do parsing dos metadados.
		// Autoplay bloqueado retorna promise rejeitada, por isso .catch vazio.
		// Se for permitido, o play dispara ontimeupdate que lê duration, e
		// depois pausamos sem som nenhum ser reproduzido (micro-timing).
		queueMicrotask(() => {
			const player = audioRef.current;
			if (!player) return;
			const promessa = player.play();
			if (promessa && typeof promessa.then === "function") {
				promessa
					.catch(() => undefined)
					.finally(() => {
						const p = audioRef.current;
						if (p && !p.paused) {
							try { p.pause(); } catch { /* no-op */ }
						}
					});
			}
		});

		return () => {
			clearTimeout(t1);
			clearTimeout(t2);
			clearTimeout(t3);
			clearTimeout(tFallback);
			clearInterval(polling);
		};
	}, [url]);

	useEffect(() => {
		const player = audioRef.current;
		if (!player) return undefined;
		player.playbackRate = audioRate;
		gravarPreferencia(LS_NAME, audioRate);
	}, [audioRate]);

	const dispararDuracao = () => {
		const el = audioRef.current;
		if (!el) return;
		const d = el.duration;
		if (Number.isFinite(d) && d > 0) {
			setDuracao(d);
			setDuracaoCarregando(false);
		}
	};

	const alternarVelocidade = () => {
		const atual = VELOCIDADES.indexOf(audioRate);
		setAudioRate(VELOCIDADES[(atual + 1) % VELOCIDADES.length] || 1);
	};

	const textoDuracao = formatarDuracao(duracao);

	return (
		<div className="flex items-center gap-1.5">
			{textoDuracao && (
				<span className="shrink-0 rounded-md bg-black/[0.03] px-1.5 py-0.5 text-[11px] tabular-nums text-muted-foreground dark:bg-white/[0.06]">
					{textoDuracao}
				</span>
			)}
			{!textoDuracao && duracaoCarregando && (
				<span className="shrink-0 w-10 rounded-md bg-black/[0.03] px-1.5 py-0.5 text-[11px] tabular-nums text-muted-foreground/60 dark:bg-white/[0.06] animate-pulse">
					0:00
				</span>
			)}
			<audio
				ref={audioRef}
				src={url}
				controls
				preload="auto"
				className="max-w-full"
				// Manipuladores de evento COMO PROPS REACT (não addEventListener).
				// Diferente do effect com dependências [], eles SEMPRE rodam no
				// elemento atual, mesmo que a url mude e o React reutilize a DOM
				// node entre mensagens diferentes (garante disparar duração).
				onLoadedMetadata={dispararDuracao}
				onDurationChange={dispararDuracao}
				onCanPlay={dispararDuracao}
				onProgress={dispararDuracao}
				onLoadedData={dispararDuracao}
				onTimeUpdate={dispararDuracao}
				onPlaying={() => {
					setShowButtonRate(true);
					dispararDuracao();
				}}
				onPause={() => setShowButtonRate(false)}
				onEnded={() => setShowButtonRate(false)}
			/>
			{showButtonRate && (
				<Button
					variant="ghost"
					size="sm"
					className="h-7 shrink-0 px-2 text-xs tabular-nums"
					onClick={alternarVelocidade}
				>
					{audioRate}x
				</Button>
			)}
		</div>
	);
}
